import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from './Modal';
import { Bug, Lightbulb, Camera, X, Loader2, Check, Maximize2, Undo2 } from 'lucide-react';
import { captureFeedbackContext, type FeedbackContext } from '../../lib/utils/bugReportContext';
import { captureScreenshot } from '../../lib/utils/captureScreenshot';
import {
  addMark,
  annotateScreenshot,
  markRadius,
  relativePoint,
  MARK_COLOR,
  MAX_MARKS,
  type ScreenshotMark,
} from '../../lib/utils/screenshotMarks';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import toast from 'react-hot-toast';

export type FeedbackType = 'bug' | 'feature';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: FeedbackType;
  screenshotBlob?: Blob | null;
  initialDescription?: string;
  /** Stack dell'errore che ha aperto la segnalazione automatica (finisce nel context) */
  errorStack?: string;
}

const CONFIG: Record<FeedbackType, {
  title: string;
  icon: typeof Bug;
  iconColor: string;
  iconBg: string;
  subtitle: string;
  label: string;
  placeholder: string;
  submitText: string;
  successText: string;
}> = {
  bug: {
    title: 'Segnala un problema',
    icon: Bug,
    iconColor: '#EB5757',
    iconBg: 'rgba(235, 87, 87, 0.08)',
    subtitle: 'Aiutaci a migliorare descrivendo cosa è successo. Le informazioni tecniche vengono catturate automaticamente.',
    label: 'Descrizione del problema',
    placeholder: 'Cosa stavi facendo quando si è verificato il problema?',
    submitText: 'Invia segnalazione',
    successText: 'Segnalazione inviata',
  },
  feature: {
    title: 'Richiedi una funzionalità',
    icon: Lightbulb,
    iconColor: '#9B59B6',
    iconBg: 'rgba(155, 89, 182, 0.08)',
    subtitle: 'Hai un\'idea per migliorare il CRM? Descrivila qui sotto e la valuteremo.',
    label: 'Descrizione della funzionalità',
    placeholder: 'Descrivi la funzionalità che vorresti vedere e perché sarebbe utile...',
    submitText: 'Invia richiesta',
    successText: 'Richiesta inviata',
  },
};

/** Cerchi rossi sopra l'immagine mostrata (stessa forma che finisce nel PNG caricato). */
function MarksOverlay({ marks, size }: { marks: ScreenshotMark[]; size: { w: number; h: number } | null }) {
  if (!size || marks.length === 0) return null;
  const diameterPct = (markRadius(size.w, size.h) * 2 / size.w) * 100;
  return (
    <>
      {marks.map((m, i) => (
        <span
          key={i}
          className="absolute pointer-events-none rounded-full"
          style={{
            left: `${m.x * 100}%`,
            top: `${m.y * 100}%`,
            width: `${diameterPct}%`,
            aspectRatio: '1 / 1',
            transform: 'translate(-50%, -50%)',
            border: `3px solid ${MARK_COLOR}`,
            boxShadow: '0 0 0 1px rgba(255,255,255,0.6)',
          }}
        />
      ))}
    </>
  );
}

export function FeedbackModal({ isOpen, onClose, type, screenshotBlob, initialDescription, errorStack }: FeedbackModalProps) {
  const { user, organizationId } = useAuth();
  const [description, setDescription] = useState('');
  const [context, setContext] = useState<FeedbackContext | null>(null);
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Punti indicati dall'utente sullo screenshot (disegnati nel PNG al momento dell'invio)
  const [marks, setMarks] = useState<ScreenshotMark[]>([]);
  const [imageSize, setImageSize] = useState<{ w: number; h: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cfg = CONFIG[type];
  const Icon = cfg.icon;

  useEffect(() => {
    if (isOpen) {
      const baseContext = captureFeedbackContext(user);
      setContext(errorStack ? { ...baseContext, errorStack } : baseContext);
      setDescription(initialDescription || '');
      setSubmitted(false);
      setMarks([]);

      if (screenshotBlob) {
        setScreenshot(screenshotBlob);
        setScreenshotPreview(URL.createObjectURL(screenshotBlob));
      } else {
        setScreenshot(null);
        setScreenshotPreview(null);
      }

      setTimeout(() => textareaRef.current?.focus(), 100);
    }

    return () => {
      if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    };
  }, [isOpen]);

  const handleCaptureScreenshot = async () => {
    try {
      // Nascondi modale, cattura, rimostra
      const modalRoot = document.getElementById('modal-root');
      if (modalRoot) modalRoot.style.display = 'none';

      const blob = await captureScreenshot();

      if (modalRoot) modalRoot.style.display = '';

      if (blob) {
        setScreenshot(blob);
        setScreenshotPreview(URL.createObjectURL(blob));
        setMarks([]);
      }
    } catch {
      toast.error('Impossibile catturare lo screenshot');
    }
  };

  const removeScreenshot = () => {
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshot(null);
    setScreenshotPreview(null);
    setMarks([]);
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    e.stopPropagation();
    const point = relativePoint(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());
    if (point) setMarks(prev => addMark(prev, point));
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);

    try {
      let screenshotUrl: string | null = null;

      if (screenshot) {
        const fileName = `user-feedback/${crypto.randomUUID()}.png`;
        const toUpload = await annotateScreenshot(screenshot, marks);
        const { error: uploadError } = await supabase.storage
          .from('uploads')
          .upload(fileName, toUpload, { contentType: 'image/png' });

        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('uploads').getPublicUrl(fileName);
          screenshotUrl = urlData.publicUrl;
        }
      }

      const { error } = await supabase.from('user_feedback').insert({
        organization_id: organizationId,
        user_id: user?.id,
        user_email: user?.email,
        type,
        description: description.trim(),
        context: context || {},
        screenshot_url: screenshotUrl,
      });

      if (error) throw error;

      setSubmitted(true);
      toast.success(cfg.successText);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      console.error('Feedback submit error:', err);
      toast.error('Errore nell\'invio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} title={cfg.title} size="md">
      <div className="py-2">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5">
          <div
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ background: cfg.iconBg }}
          >
            <Icon className="w-[18px] h-[18px]" style={{ color: cfg.iconColor }} />
          </div>
          <p className="text-sm" style={{ color: '#787774' }}>
            {cfg.subtitle}
          </p>
        </div>

        {/* Textarea */}
        <div className="mb-4">
          <label className="block text-xs font-medium mb-1.5 dark:text-gray-300" style={{ color: '#37352F' }}>
            {cfg.label}
          </label>
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={cfg.placeholder}
            rows={4}
            className="w-full px-3 py-2.5 text-sm rounded-lg resize-none transition-all outline-none dark:bg-[#3F4447] dark:text-white dark:border-[#4F5457]"
            style={{
              border: '1px solid rgba(55,53,47,0.16)',
              color: '#37352F',
              background: '#FBFBFA',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'rgba(35, 131, 226, 0.57)';
              e.target.style.boxShadow = 'rgba(35, 131, 226, 0.14) 0px 0px 0px 3px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(55,53,47,0.16)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* Screenshot */}
        <div className="mb-4">
          {screenshotPreview ? (
            <>
            <div className="relative inline-block group">
              <img
                src={screenshotPreview}
                alt="Screenshot"
                onClick={() => setLightboxOpen(true)}
                onLoad={(e) => setImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                className="rounded-lg border max-h-32 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                style={{ borderColor: 'rgba(55,53,47,0.09)' }}
              />
              <MarksOverlay marks={marks} size={imageSize} />
              <div
                onClick={() => setLightboxOpen(true)}
                className="absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                style={{ background: 'rgba(0,0,0,0.3)' }}
              >
                <Maximize2 className="w-4 h-4 text-white" />
              </div>
              <button
                onClick={removeScreenshot}
                className="absolute -top-2 -right-2 w-5 h-5 bg-white dark:bg-gray-700 rounded-full flex items-center justify-center shadow-sm border"
                style={{ borderColor: 'rgba(55,53,47,0.09)' }}
              >
                <X className="w-3 h-3" style={{ color: '#787774' }} />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block mt-1.5 text-xs hover:underline"
              style={{ color: '#787774' }}
            >
              {marks.length > 0
                ? `${marks.length === 1 ? '1 punto indicato' : `${marks.length} punti indicati`} · modifica`
                : 'Indica il punto esatto sullo screenshot'}
            </button>
            </>
          ) : (
            <button
              onClick={handleCaptureScreenshot}
              className="flex items-center gap-2 px-3 py-2 text-xs rounded-lg transition-colors hover:bg-[#F7F7F5] dark:hover:bg-[#3F4447]"
              style={{
                color: '#787774',
                border: '1px dashed rgba(55,53,47,0.16)',
              }}
            >
              <Camera className="w-3.5 h-3.5" />
              Allega screenshot
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-3" style={{ borderTop: '1px solid rgba(55,53,47,0.09)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg transition-colors hover:bg-[#F7F7F5] dark:hover:bg-[#3F4447] dark:text-gray-300"
            style={{
              color: '#37352F',
              border: '1px solid rgba(55,53,47,0.16)',
            }}
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={!description.trim() || submitting || submitted}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: submitted ? '#34C759' : type === 'bug' ? '#2563EB' : '#9B59B6',
            }}
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Invio...
              </span>
            ) : submitted ? (
              <span className="flex items-center gap-2">
                <Check className="w-3.5 h-3.5" />
                Inviata
              </span>
            ) : (
              cfg.submitText
            )}
          </button>
        </div>
      </div>
    </Modal>

      {/* Lightbox screenshot — portal per renderizzare sopra il modale */}
      {lightboxOpen && screenshotPreview && createPortal(
        <div
          className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 cursor-pointer"
          onClick={() => setLightboxOpen(false)}
        >
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-3 py-2 rounded-lg bg-black/60 text-white text-xs cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <span>
              {marks.length >= MAX_MARKS
                ? `Massimo ${MAX_MARKS} punti`
                : 'Clicca sull\'immagine per cerchiare in rosso il punto del problema'}
            </span>
            {marks.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setMarks(prev => prev.slice(0, -1))}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  <Undo2 className="w-3 h-3" />
                  Annulla ultimo
                </button>
                <button
                  type="button"
                  onClick={() => setMarks([])}
                  className="px-2 py-1 rounded bg-white/10 hover:bg-white/20"
                >
                  Togli tutti
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              className="px-2 py-1 rounded bg-white text-black font-medium hover:bg-white/90"
            >
              Fatto
            </button>
          </div>
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={screenshotPreview}
              alt="Screenshot ingrandito"
              className="block max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl cursor-crosshair"
              onClick={handleImageClick}
              onLoad={(e) => setImageSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
            />
            <MarksOverlay marks={marks} size={imageSize} />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
